import { Badge, DataTable, Stack, TextStyle, Tooltip } from "@shopify/polaris";
import React, { useCallback, useReducer } from "react";
import styled from "styled-components";

import { get } from "lodash";
import {
    formatTime,
    handleError,
    objectInvalid,
    reducerFn,
} from "../../helper";
import {
    AMAZON_IDEA_PRODUCT_STATUS,
    AMAZON_IDEA_PRODUCT_STATUS_BADGE,
    AMAZON_IDEA_STATUS,
} from "../../variable";
import ModalImageClaimPolaris from "../seller/ModalImageClaimPolaris";
import ElementScrollable from "../shared/ElementScrollable";
import EmptyStatePolaris from "../shared/EmptyStatePolaris";
import PaginationPolaris from "../shared/PaginationPolaris";
import SelectRow from "../shared/SelectRow";
import SkeletonPagePolaris from "../shared/SkeletonPagePolaris";
import AmazonIdeaMarkBadge from "./actions/amazon-idea-mark-badge";
import AmazonIdeaUpdateSingleProductStatus from "./actions/amazon-idea-update-single-product-status";
import AmazonIdeaAction from "./amazon-idea-action";
import AmazonIdeaBulkAction from "./amazon-idea-bulk-action";
import { useAmazonIdeaCtx } from "./context";
import { STATUSES_BADGE } from "./helper";

function AmazonIdeaTable() {
    const {
        loading,
        error,
        amazonIdeas,
        setFilter,
        filter,
        total,
        refetch,
        aggregations,
    } = useAmazonIdeaCtx();

    const handlePaginationChange = useCallback(
        (offset, limit) => {
            setFilter({ offset, limit });
        },
        [setFilter],
    );

    let { limit, offset } = filter;
    const totalPage = Math.ceil(total / limit);
    const page = offset / limit + 1;
    const aggregation = {
        page,
        totalPage,
        offset,
        limit,
        onChange: handlePaginationChange,
        total,
    };

    const isDone = filter?.status === AMAZON_IDEA_STATUS.Done;
    const totalKpi = get(aggregations, "total_kpi");

    return (
        <>
            <Wrapper>
                {loading ? (
                    <SkeletonPagePolaris />
                ) : error ? (
                    <div>Error: {handleError(error?.toString())}</div>
                ) : amazonIdeas?.length > 0 ? (
                    <>
                        <SelectRow
                            data={amazonIdeas}
                            refetch={refetch}
                            actionMarkup={<AmazonIdeaBulkAction />}
                        >
                            {({ checkAllMarkup, checkItemMarkup }) => (
                                <ElementScrollable fixedColumn>
                                    <DataTable
                                        rows={
                                            getRows(
                                                amazonIdeas,
                                                checkItemMarkup,
                                            ) || []
                                        }
                                        columnContentTypes={[
                                            ...Array.from({
                                                length: 13,
                                            }).fill("text"),
                                            "numeric",
                                        ]}
                                        headings={[
                                            checkAllMarkup,
                                            "ID",
                                            "Title",
                                            "Mockup",
                                            "Type",
                                            "Status",
                                            "Personalized",
                                            "KPI",
                                            "Product",
                                            "Author",
                                            "Picker",
                                            "Stores",
                                            // "Store Type",
                                            "Created At",
                                            <div className="actions-wrap">
                                                Actions
                                            </div>,
                                        ]}
                                        hideScrollIndicator
                                        verticalAlign="middle"
                                    />
                                </ElementScrollable>
                            )}
                        </SelectRow>
                        <PaginationContainer>
                            <PaginationPolaris
                                aggregation={aggregation}
                                showTotal
                                {...(isDone ? { totalKpi } : {})}
                            />
                        </PaginationContainer>
                    </>
                ) : (
                    <EmptyStatePolaris />
                )}
            </Wrapper>
        </>
    );
}

function getRows(nodes, checkItemMarkup) {
    const res = [];

    for (const node of nodes) {
        if (objectInvalid(node)) continue;

        const {
            id,
            title,
            type,
            createdAt,
            status,
            isPersonalized,
            // asin,
            // productLink,
            totalStore,
        } = node;

        let kpi = Number((totalStore || 1) * (node.kpi || 0))
        kpi = Number(kpi).toFixed(2)
        const statusText = STATUSES_BADGE[status];
        const isPersonalizedMarkup = !!isPersonalized ? "success" : "new";
        const isPersonalizedText = !!isPersonalized ? "Yes" : "No";

        let statusBadge = (
            <Badge
                status={statusText}
                progress={
                    status === AMAZON_IDEA_STATUS.Doing
                        ? "incomplete"
                        : undefined
                }
            >
                {status}
            </Badge>
        );

        if (status === AMAZON_IDEA_STATUS.Rejected && node.rejectReason) {
            statusBadge = (
                <>
                    <Tooltip content={node.rejectReason}>{statusBadge}</Tooltip>
                    <div style={{ paddingLeft: ".8rem" }}>
                        Reason:{" "}
                        <TextStyle variation="subdued">
                            {node.rejectReason}
                        </TextStyle>
                    </div>
                </>
            );
        }

        if (status === AMAZON_IDEA_STATUS.Denied && node.denyReason) {
            statusBadge = (
                <>
                    <Tooltip content={node.denyReason}> {statusBadge}</Tooltip>
                    <div style={{ paddingLeft: ".8rem" }}>
                        Reason:{" "}
                        <TextStyle variation="subdued">
                            {node.denyReason}
                        </TextStyle>
                    </div>
                </>
            );
        }

        const storeNames = getStoreNames(node);
        const storeTypes = getStoreTypes(node);
        const item = [
            checkItemMarkup ? checkItemMarkup(id) : null,
            <div className="id item">
                <span>{id}</span>
            </div>,
            <div className="title item">
                <span>{title}</span>
                <AmazonIdeaMarkBadge ids={[id]} node={node} />
            </div>,
            <div className="mockup item">
                <div>
                    {node?.mockups?.length > 0 ? (
                        <ModalImageClaimPolaris
                            files={{
                                files: node.mockups,
                            }}
                            limit={1}
                            width={80}
                            height={80}
                            label="Mockups"
                        />
                    ) : null}
                </div>
            </div>,
            <div className="type item">
                <span>{type}</span>
            </div>,
            <div className="status item">
                {/* <Badge
                    status={statusText}
                    progress={
                        status === AMAZON_IDEA_STATUS.Doing
                            ? "incomplete"
                            : undefined
                    }
                >
                    {status}
                </Badge> */}
                {statusBadge}
                {/* {status === AMAZON_IDEA_STATUS.Denied && node.denyReason ? (
                    <div>
                        Reason:{" "}
                        <TextStyle variation="subdued">
                            {node.denyReason}
                        </TextStyle>
                    </div>
                ) : null} */}
            </div>,
            <div className="personalized item">
                <Badge status={isPersonalizedMarkup}>
                    {isPersonalizedText}
                </Badge>
            </div>,
            // <div className="kpi item">{node.kpi > 0 ? node.kpi : ""}</div>,
            <div
                className="kpi item"
                data-kpi={node.kpi}
                data-total-store={totalStore}
            >
                {kpi ? kpi : ""}
            </div>,
            <div className="product item">
                {/* <ProductLink productLink={productLink} asin={asin} /> */}
                <ProductInfo node={node} />
            </div>,
            <div className="author item">
                <span>{getUser(node.author)}</span>
            </div>,
            <div className="picker item">
                <span>{getUser(node.picker)}</span>
            </div>,
            <div className="store-name item">
                <span>{storeNames}</span>
            </div>,
            // <div className="store-type item">
            //     <span>{storeTypes}</span>
            // </div>,
            <div className="created-at item">
                <span>{formatTime(createdAt)}</span>
            </div>,
            <div className="actions-wrap item">
                <AmazonIdeaAction node={node} />
            </div>,
        ];

        res.push(item);
    }

    return res;
}

function getUser(user) {
    if (!user) return "";
    return [user.firstName, user.lastName].filter(Boolean).join(" ");
}

function ProductInfo({ node }) {
    const products = getProducts(node);

    return products?.length > 0 ? (
        <Stack vertical spacing="tight">
            {products.map((product, index) => (
                <Stack
                    spacing="tight"
                    alignment="center"
                    key={`product-info-${index}`}
                >
                    <ProductStoreInfo store={product?.store} />
                    <ProductLink {...product} />
                    <ProductStatusBadge
                        status={product?.status}
                        ids={product?.id ? [product.id] : undefined}
                    />
                </Stack>
            ))}
        </Stack>
    ) : null;
}

function getProducts(node) {
    const products = get(node, "products") || [];

    const res = [];

    for (let product of products) {
        if (!product || (!product.asin && !product.productLink)) {
            continue;
        }

        res.push(product);
    }

    return res;
}

const PRODUCT_STATUS_BADGE = {
    [AMAZON_IDEA_PRODUCT_STATUS.Active]: "#bbe5b3", // bbe5b3
    [AMAZON_IDEA_PRODUCT_STATUS.Inactive]: "#fead9a", // fead9a
    [AMAZON_IDEA_PRODUCT_STATUS.OutOfStock]: "#ffea8a", // ffea8a
    [AMAZON_IDEA_PRODUCT_STATUS.Closed]: "#dfe3e8", // dfe3e8
    Unknown: "#b4e1fa",
};

function ProductStatusBadge({ status, ids }) {
    const [state, setState] = useReducer(reducerFn, {
        status: status || "Unknown",
    });

    React.useEffect(() => {
        if (!status) return;

        setState({ status: status });
    }, [status]);

    const statusColor = PRODUCT_STATUS_BADGE[state.status];
    const statusText =
        AMAZON_IDEA_PRODUCT_STATUS_BADGE[state.status] || "Unknown";

    return (
        <StatusWrapper
            statusColor={statusColor}
            style={{ "--bg-color": statusColor }}
        >
            {statusText}
            <AmazonIdeaUpdateSingleProductStatus
                status={state.status}
                setState={setState}
                ids={ids}
            />
        </StatusWrapper>
    );
}

function ProductStoreInfo({ store }) {
    if (!store) return null;

    return (
        <span title={store?.id}>
            <b>Store:</b> {store?.title} -
        </span>
    );
}

export function ProductLink({ productLink, asin }) {
    return productLink ? (
        <a
            title={`Asin: ${asin}`}
            href={productLink}
            target="_blank"
            className="Polaris-Link"
        >
            {asin ? asin : "Product Link"}
        </a>
    ) : asin ? (
        <span title={`Asin: ${asin}`}>{asin}</span>
    ) : (
        ""
    );
}

function getStoreNames(node) {
    const stores = get(node, "stores") || [];

    return stores
        .map((item) => item?.title)
        .filter(Boolean)
        .join(", ");
}

function getStoreTypes(node) {
    const stores = get(node, "storeTypes") || [];

    return stores
        .map((item) => item?.label)
        .filter(Boolean)
        .join(", ");
}

const Wrapper = styled.div`
    overflow-x: auto;

    .Polaris-DataTable__ScrollContainer {
        overflow-x: inherit;
    }

    .item {
        white-space: normal;
        word-break: break-word;
    }

    .actions-wrap {
        width: calc(13.5rem - 3.2rem);
        margin-left: auto;
    }

    .author,
    .picker,
    .store-type,
    .created-at {
        width: calc(18rem - 3.2rem);
    }

    .id {
        width: calc(13rem - 3.2rem);
    }

    .status {
        width: calc(17.5rem - 3.2rem);
    }

    .personalized,
    .type,
    .kpi {
        width: calc(13rem - 3.2rem);
    }

    .store-name {
        width: calc(25rem - 3.2rem);
    }

    .title {
        width: calc(30rem - 3.2rem);
    }

    .product {
        width: calc(40rem - 3.2rem);
    }
`;

const PaginationContainer = styled.div`
    padding: 1.6rem;
    overflow-x: scroll;
    background: var(--p-surface-subdued, #f4f6f8);
`;

const StatusWrapper = styled.div`
    display: flex;
    background-color: var(--bg-color, #f0f0f0);
    padding: 0.3rem 1rem 0.3rem 1.3rem;
    border-radius: 2rem;
    align-items: center;
    column-gap: 0.5rem;

    > div {
        display: flex;
        align-items: center;
    }
`;

export default AmazonIdeaTable;
